import { useEffect, useRef, useState } from "react";
import { Plus, Power, Save, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCustomer } from "@/lib/customer";
import { nearMatch } from "@/lib/nearMatch";
import RemoveRecord from "@/components/RemoveRecord";
import SwitchedOffList from "@/components/SwitchedOffList";
import MailDrafts from "@/components/MailDrafts";
import ReadingProfile from "@/components/ReadingProfile";

const KINDS = ["date", "text", "number", "tick", "dropdown"];
const TAT_FIELDS = [
  ["deliveryTatDays", "Delivery TAT (days)"],
  ["completionTatDays", "Completion TAT (days)"],
  ["dispatchTriggerDays", "Dispatch trigger (working days)"],
  ["followUpIntervalDays", "Follow-up interval (days)"],
  ["ldWarnDays", "LD warning window (days)"],
  ["ldPercentPerWeek", "LD per week (%)"],
];

export default function Customers() {
  const { reload, pick } = useCustomer();
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState("");
  const [draft, setDraft] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const [looksLike, setLooksLike] = useState(null);
  const stagesEnd = useRef(null);

  // this screen manages customers, so it sees the switched-off ones too
  const loadAll = async () => {
    try {
      const { data } = await api.get("/customers", { params: { all: 1 } });
      setCustomers(data.customers);
      return data.customers;
    } catch (e) {
      toast.error(errText(e));
      return [];
    }
  };

  const refresh = async () => {
    await loadAll();
    await reload();
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (!selected && customers.length) setSelected(customers[0].id);
  }, [customers, selected]);

  useEffect(() => {
    const c = customers.find((x) => x.id === selected);
    setDraft(c ? JSON.parse(JSON.stringify(c)) : null);
  }, [selected, customers]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/customers", { name, code, copyFrom: copyFrom || null });
      toast.success(`${data.name} added`);
      setName("");
      setCode("");
      setLooksLike(null);
      await refresh();
      setSelected(data.id);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const createChecked = () => {
    const match = nearMatch(name, code, customers);
    if (match) {
      setLooksLike(match);
      return;
    }
    create();
  };

  const removeCustomer = async () => {
    setBusy(true);
    try {
      await api.delete(`/customers/${draft.id}`);
      toast.success(`${draft.name} deleted — it is gone from everyone's picker`);
      setSelected("");
      await refresh();
    } catch (e) {
      toast.error(errText(e), { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (on, customer = draft) => {
    setBusy(true);
    try {
      await api.patch(`/customers/${customer.id}`, { isActive: on });
      toast.success(on
        ? `${customer.name} is back on`
        : `${customer.name} switched off — its POs stay readable`);
      await refresh();
    } catch (e) {
      toast.error(errText(e), { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/customers/${draft.id}`, {
        name: draft.name,
        code: draft.code,
        workflow: draft.workflow,
        tats: draft.tats,
        itemTypes: draft.itemTypes,
      });
      toast.success("Workflow saved");
      await refresh();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const setStage = (index, patch) =>
    setDraft((d) => {
      const stages = [...d.workflow.stages];
      stages[index] = { ...stages[index], ...patch };
      return { ...d, workflow: { stages } };
    });

  const move = (index, by) =>
    setDraft((d) => {
      const stages = [...d.workflow.stages];
      const to = index + by;
      if (to < 0 || to >= stages.length) return d;
      [stages[index], stages[to]] = [stages[to], stages[index]];
      return { ...d, workflow: { stages } };
    });

  const addStage = () => {
    setDraft((d) => {
      const nextId = Math.max(0, ...d.workflow.stages.map((s) => Number(s.id) || 0)) + 1;
      const key = `stage${nextId}Date`;
      return {
        ...d,
        workflow: {
          stages: [
            ...d.workflow.stages.slice(0, -1),
            {
              id: nextId,
              label: "New stage",
              action: "What to record now",
              // a stage needs the field that completes it, so it arrives with one ready
              fields: [{ key, label: "Date recorded", kind: "date", required: true }],
              reminders: [],
              completeField: key,
              allowedDays: null,
            },
            d.workflow.stages[d.workflow.stages.length - 1],
          ],
        },
      };
    });
    setTimeout(() => stagesEnd.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const removeStage = (index) =>
    setDraft((d) => {
      if (d.workflow.stages.length <= 2) {
        toast.error("A workflow needs at least two stages, the last one being the closed stage.");
        return d;
      }
      return { ...d, workflow: { stages: d.workflow.stages.filter((_, i) => i !== index) } };
    });

  const setField = (si, fi, patch) =>
    setDraft((d) => {
      const stages = [...d.workflow.stages];
      const fields = [...(stages[si].fields || [])];
      fields[fi] = { ...fields[fi], ...patch };
      stages[si] = { ...stages[si], fields };
      return { ...d, workflow: { stages } };
    });

  const addField = (si) =>
    setDraft((d) => {
      const stages = [...d.workflow.stages];
      const key = `field_${Date.now().toString(36)}`;
      stages[si] = {
        ...stages[si],
        fields: [...(stages[si].fields || []), { key, label: "New field", kind: "text", options: [] }],
      };
      return { ...d, workflow: { stages } };
    });

  const removeField = (si, fi) =>
    setDraft((d) => {
      const stages = [...d.workflow.stages];
      stages[si] = { ...stages[si], fields: stages[si].fields.filter((_, i) => i !== fi) };
      return { ...d, workflow: { stages } };
    });

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Customers and workflows</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Each customer carries its own stages, its own fields and its own TATs. Nothing is shared but the people.
        </p>
      </div>

      <div className="panel p-3 sm:p-4">
        <h2 className="text-sm font-medium">Add a customer</h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <Label className="field-label">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-56" data-testid="customer-name-input" />
          </div>
          <div>
            <Label className="field-label">Short code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="h-9 w-28 font-mono" data-testid="customer-code-input" />
          </div>
          <div>
            <Label className="field-label">Start its workflow from</Label>
            <select
              value={copyFrom}
              onChange={(e) => setCopyFrom(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="customer-copy-from"
            >
              <option value="">The standard seven-stage flow</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <Button onClick={createChecked} disabled={busy || !name || !code} data-testid="customer-create-button">
            <Plus className="mr-2 h-4 w-4" />
            Add the customer
          </Button>
        </div>
      </div>

      <Dialog open={!!looksLike} onOpenChange={(v) => !v && setLooksLike(null)}>
        <DialogContent data-testid="customer-near-match-dialog">
          <DialogHeader>
            <DialogTitle>{looksLike?.name} already exists</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {looksLike?.name} ({looksLike?.code}) is close enough to “{name}” ({code}) that one of
            them is probably a duplicate. Open the one you already have, or add this anyway if both
            are real.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSelected(looksLike.id);
                setLooksLike(null);
              }}
              data-testid="customer-open-existing-button"
            >
              Open {looksLike?.name} instead
            </Button>
            <Button onClick={create} disabled={busy} data-testid="customer-create-anyway-button">
              Add it anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="panel flex flex-wrap items-end gap-3 p-3">
        <div>
          <Label className="field-label">Working on</Label>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="customer-select"
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {`${c.name} (${c.code})${c.isActive === false ? " — off" : ""}`}
              </option>
            ))}
          </select>
        </div>
        {draft && (
          <>
            <Button onClick={save} disabled={busy} data-testid="workflow-save-button">
              <Save className="mr-2 h-4 w-4" />
              Save this workflow
            </Button>
            <Button variant="outline" onClick={() => pick(draft.id)} disabled={draft.isActive === false} data-testid="workflow-switch-button">
              Work inside {draft.name}
            </Button>
            {draft.isActive === false && (
              <Button variant="outline" onClick={() => toggleActive(true)} disabled={busy} data-testid="customer-switch-on-button">
                <Power className="mr-2 h-4 w-4" />
                Switch {draft.name} back on
              </Button>
            )}
          </>
        )}
      </div>

      {draft && draft.isActive !== false && (
        <div className="panel border-red-200 p-3 dark:border-red-900/60 sm:p-4">
          <h2 className="text-sm font-medium">Removing {draft.name}</h2>
          <div className="mt-3">
            <RemoveRecord
              kind="customer"
              name={draft.name}
              confirmValue={draft.code}
              poLines={draft.poLines || 0}
              busy={busy}
              onDelete={removeCustomer}
              onSwitchOff={() => toggleActive(false)}
              testId="customer-remove-button"
            />
          </div>
        </div>
      )}

      <SwitchedOffList
        title="Switched-off customers"
        records={customers
          .filter((c) => c.isActive === false)
          .map((c) => ({ ...c, tag: c.code }))}
        busy={busy}
        onBringBack={(c) => toggleActive(true, c)}
        testId="switched-off-customers"
      />

      {draft && (
        <>
          <div className="panel p-3 sm:p-4">
            <h2 className="text-sm font-medium">TATs for {draft.name}</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {TAT_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <Label className="field-label">{label}</Label>
                  <Input
                    type="number"
                    value={draft.tats?.[key] ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, tats: { ...d.tats, [key]: Number(e.target.value) } }))}
                    className="h-9 font-mono tabular-nums"
                    data-testid={`tat-${key}-input`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Label className="field-label">Escalation days (comma separated)</Label>
              <Input
                value={(draft.tats?.escalationDays || []).join(", ")}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    tats: {
                      ...d.tats,
                      escalationDays: e.target.value.split(",").map((v) => Number(v.trim()) || 0).filter(Boolean),
                    },
                  }))
                }
                className="h-9 w-64 font-mono tabular-nums"
                data-testid="tat-escalation-input"
              />
            </div>
          </div>

          <div className="panel p-3 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-medium" data-testid="stage-count">
                Stages, in order — {draft.workflow.stages.length} stage{draft.workflow.stages.length === 1 ? "" : "s"}
              </h2>
              <Button variant="outline" size="sm" onClick={addStage} data-testid="add-stage-button">
                <Plus className="mr-2 h-4 w-4" />
                Add a stage
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              The last stage is where closed POs sit. Every other stage needs the field that completes it — fill that
              field and the PO moves on, clear it and it moves back.
            </p>

            <div className="mt-3 space-y-3">
              {draft.workflow.stages.map((stage, si) => (
                <div key={stage.id} className="rounded-md border border-border p-3" data-testid={`stage-row-${stage.id}`}>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[180px]">
                      <Label className="field-label">Stage name</Label>
                      <Input
                        value={stage.label || ""}
                        onChange={(e) => setStage(si, { label: e.target.value })}
                        className="h-9"
                        data-testid={`stage-label-input-${stage.id}`}
                      />
                    </div>
                    <div className="flex-[2] min-w-[240px]">
                      <Label className="field-label">What to record now</Label>
                      <Input
                        value={stage.action || ""}
                        onChange={(e) => setStage(si, { action: e.target.value })}
                        className="h-9"
                        data-testid={`stage-action-input-${stage.id}`}
                      />
                    </div>
                    <div>
                      <Label className="field-label">Completed by</Label>
                      <select
                        value={stage.completeField || ""}
                        onChange={(e) => setStage(si, { completeField: e.target.value || null })}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid={`stage-complete-field-${stage.id}`}
                      >
                        <option value="">Nothing — this is the closed stage</option>
                        {(stage.fields || []).map((f) => (
                          <option key={f.key} value={f.key}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="field-label">Allowed days</Label>
                      <Input
                        type="number"
                        value={stage.allowedDays ?? ""}
                        onChange={(e) => setStage(si, { allowedDays: e.target.value === "" ? null : Number(e.target.value) })}
                        className="h-9 w-24 font-mono tabular-nums"
                        data-testid={`stage-allowed-days-${stage.id}`}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => move(si, -1)} className="rounded-md p-2 hover:bg-secondary" data-testid={`stage-up-${stage.id}`} aria-label="Move up">
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button onClick={() => move(si, 1)} className="rounded-md p-2 hover:bg-secondary" data-testid={`stage-down-${stage.id}`} aria-label="Move down">
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button onClick={() => removeStage(si)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-testid={`stage-remove-${stage.id}`} aria-label="Remove stage">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 rounded-md bg-secondary/30 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium">Fields recorded here</span>
                      <Button variant="outline" size="sm" onClick={() => addField(si)} data-testid={`add-field-${stage.id}`}>
                        Add a field
                      </Button>
                    </div>
                    {(stage.fields || []).length === 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">No fields yet.</p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {stage.fields.map((f, fi) => (
                          <div key={f.key} className="flex flex-wrap items-end gap-2" data-testid={`field-row-${f.key}`}>
                            <div className="flex-1 min-w-[160px]">
                              <Label className="field-label">Label</Label>
                              <Input
                                value={f.label || ""}
                                onChange={(e) => setField(si, fi, { label: e.target.value })}
                                className="h-8"
                                data-testid={`field-label-${f.key}`}
                              />
                            </div>
                            <div>
                              <Label className="field-label">Kind</Label>
                              <select
                                value={f.kind || "text"}
                                onChange={(e) => setField(si, fi, { kind: e.target.value })}
                                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                                data-testid={`field-kind-${f.key}`}
                              >
                                {KINDS.map((k) => (
                                  <option key={k} value={k}>{k}</option>
                                ))}
                              </select>
                            </div>
                            {f.kind === "dropdown" && (
                              <div className="flex-1 min-w-[180px]">
                                <Label className="field-label">Options (comma separated)</Label>
                                <Input
                                  value={(f.options || []).join(", ")}
                                  onChange={(e) => setField(si, fi, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })}
                                  className="h-8"
                                  data-testid={`field-options-${f.key}`}
                                />
                              </div>
                            )}
                            <label className="flex items-center gap-2 pb-2 text-xs">
                              <input
                                type="checkbox"
                                checked={!!f.required}
                                onChange={(e) => setField(si, fi, { required: e.target.checked })}
                                data-testid={`field-required-${f.key}`}
                              />
                              Needed
                            </label>
                            <button
                              onClick={() => removeField(si, fi)}
                              className="pb-2 text-muted-foreground hover:text-red-600"
                              data-testid={`field-remove-${f.key}`}
                              aria-label="Remove field"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-2 text-xs text-muted-foreground" data-testid={`stage-reminders-${stage.id}`}>
                    {(stage.reminders || []).length === 0
                      ? "No reminders chase this stage."
                      : `Chases: ${(stage.reminders || []).map((r) => r.title).join(" · ")}`}
                  </div>
                </div>
              ))}
              <div ref={stagesEnd} />
            </div>
          </div>

          <MailDrafts customer={draft} onSaved={reload} />
          <ReadingProfile customer={draft} onSaved={reload} />
        </>
      )}
    </div>
  );
}
